import {
  HttpErrorResponse,
  type HttpEvent,
  type HttpHandlerFn,
  type HttpInterceptorFn,
  type HttpRequest,
} from '@angular/common/http';
import { catchError, from, of, switchMap, throwError } from 'rxjs';
import type { Observable } from 'rxjs';
import { NoInternetConnectionError } from './http-error/no-internet-connection-error/no-internet-connection.error';
import { NotFoundEndpointError } from './http-error/not-found-endpoint-error/not-found-endpoint.error';
import { NotFoundServerError } from './http-error/not-found-server-error/not-found-server.error';
import { MaintenanceModeError } from './http-error/maintenance-mode-error/maintenance-mode.error';
import { PctHttpErrorResponse } from './pct-http-error-response';
import { DISABLE_HTTP_GLOBAL_ERROR_TO_BE_HANDLED } from '../disable-error-code';
import { inject, Injector, runInInjectionContext } from '@angular/core';
import { I18nProviderInjectionToken } from '../i18n/i18n.scope';
import { Router } from '@angular/router';
import { HandledErrorResponse } from './handled-error-response';
import { isFunction, isNumber } from '@pct-ts/typeguard';
import { HTTP_ERROR_OPTIONS, type HttpErrorOptions } from '../http-error-options';
import { handleNoInternetConnectionError } from './http-error/no-internet-connection-error/handle-no-internet-connection-error';
import { handleNotFoundEndpointError } from './http-error/not-found-endpoint-error/handle-not-found-endpoint-error';
import { handleMaintenanceModeError } from './http-error/maintenance-mode-error/handle-maintenance-mode-error';
import { handleNotFoundServerError } from './http-error/not-found-server-error/handle-not-found-server-error';
import { isBackendErrorWithMessage } from './typeguard/is-backend-error-with-message';

/**
 * Transforms an `HttpErrorResponse` instance into a specific `PtHttpErrorResponse` subtype or falls back to the generic `PtHttpErrorResponse`.
 *
 * @param {HttpErrorResponse} error - The error response object to transform, containing the HTTP status and other relevant error details.
 * @param options
 * @return {PctHttpErrorResponse} A specific `PtHttpErrorResponse` subtype such as `NoInternetConnectionError`, `NotFoundEndpointError`, `MaintenanceModeError`, `NotFoundServerError`, or the generic `PtHttpErrorResponse`.
 */
function transformHttpErrorToPtHttpErrorResponse(
  error: PctHttpErrorResponse,
  options: HttpErrorOptions | null,
): Observable<PctHttpErrorResponse> {
  const { message, status, headers, response } = error;

  return from(
    (async function (): Promise<PctHttpErrorResponse> {
      let result: PctHttpErrorResponse;

      switch (status) {
        case 0:
          result = new NoInternetConnectionError(message, status, response, headers, error);
          break;
        case 404:
          if (isBackendErrorWithMessage(response)) {
            result = error;
            break;
          }

          if (response instanceof Blob) {
            try {
              const text = await response.text();
              const parsed = JSON.parse(text);
              result = isBackendErrorWithMessage(parsed)
                ? error
                : new NotFoundEndpointError(message, status, text, headers, error);
            } catch {
              result = new NotFoundEndpointError(message, status, message, headers, error);
            }
            break;
          }

          result = new NotFoundEndpointError(message, status, message, headers, error);
          break;
        case 503:
          result = new MaintenanceModeError(message, status, response, headers, error);
          break;
        case 504:
          result = new NotFoundServerError(message, status, response, headers, error);
          break;
        default:
          result = error;
      }

      return result;
    })(),
  ).pipe(
    switchMap(result => {
      // Let application-specific transformer create custom error classes.
      if (result === error && isFunction(options?.transformHttpErrorToPtHttpErrorResponseFn)) {
        return options.transformHttpErrorToPtHttpErrorResponseFn(error, options);
      }

      return of(result);
    }),
  );
}

/**
 * Handles global errors, such as no internet connection or not found endpoint errors,
 * by displaying appropriate dialogs to the user, and performing actions such as page reloads.
 * (run in injection context)
 *
 * @param {PctHttpErrorResponse} error - The error object received from an HTTP request.
 * @return {Observable<PctHttpErrorResponse>} An observable that emits the error object or performs side effects such as displaying dialogs and reloading the window.
 */
function handleIdentifiedError(
  error: PctHttpErrorResponse,
  options: HttpErrorOptions | null,
): Observable<PctHttpErrorResponse> {
  const router = inject(Router);
  const i18nScope = options?.i18n?.prefix ?? I18nProviderInjectionToken.scope;
  const i18nReloadKey =
    options?.i18n?.reloadKey ?? `${I18nProviderInjectionToken.scope}.COMMON.RELOAD`;
  const { message, status, headers, response } = error;

  if (error instanceof NoInternetConnectionError) {
    return handleNoInternetConnectionError(i18nScope, i18nReloadKey, error);
  }
  if (error instanceof NotFoundEndpointError) {
    return handleNotFoundEndpointError(
      i18nScope,
      router,
      message,
      status,
      response,
      headers,
      error,
    );
  }
  if (error instanceof MaintenanceModeError) {
    return handleMaintenanceModeError(i18nScope, i18nReloadKey, error);
  }
  if (error instanceof NotFoundServerError) {
    return handleNotFoundServerError(i18nScope, i18nReloadKey, error);
  }
  // Handle DI delegated handler
  if (isFunction(options?.handleIdentifiedErrorFn)) {
    return options.handleIdentifiedErrorFn(error, options);
  }

  return of(error);
}

/**
 * Interceptor function to handle HTTP errors globally. It processes HTTP requests and responses,
 * intercepts errors, and applies specified error handling logic.
 *
 * @param {HttpRequest<unknown>} req - The incoming HTTP request.
 * @param {HttpHandlerFn} next - The next handler in the HTTP pipeline.
 * @returns {Observable<HttpEvent<unknown>>} An observable that emits HTTP events, with
 *          intercepted and processed errors when applicable.
 */
export const httpErrorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const injector = inject(Injector);
  const options = inject(HTTP_ERROR_OPTIONS, { optional: true });
  const { codes: disableGlobalErrorCodes } = req.context.get(
    DISABLE_HTTP_GLOBAL_ERROR_TO_BE_HANDLED,
  );

  return next(req).pipe(
    catchError((error: any): Observable<HttpEvent<unknown>> => {
      const isDisableGlobalErrorCodesAll = disableGlobalErrorCodes === 'ALL';
      if (isDisableGlobalErrorCodesAll || error instanceof HttpErrorResponse) {
        const { message, status, headers } = error;
        return transformHttpErrorToPtHttpErrorResponse(
          new PctHttpErrorResponse(message, status, error.error, headers, error),
          options,
        ).pipe(
          switchMap(pctHttpErrorResponse => {
            let disableErrorCodes = disableGlobalErrorCodes;
            if (!Array.isArray(disableErrorCodes) && isNumber(disableErrorCodes)) {
              disableErrorCodes = [disableErrorCodes];
            } else {
              disableErrorCodes = [];
            }
            // If the error code is in the disableErrorCodes array, then return the error as is
            // else run the handleIdentifiedError function
            if (
              isDisableGlobalErrorCodesAll ||
              disableErrorCodes.includes(pctHttpErrorResponse.status)
            ) {
              const { message, status, headers } = error;
              return of(
                new HandledErrorResponse(
                  message,
                  status,
                  error.error,
                  headers,
                  pctHttpErrorResponse,
                ),
              );
            }
            return runInInjectionContext(injector, () =>
              handleIdentifiedError(pctHttpErrorResponse, options),
            );
          }),
          // Need it for type safety, because if we put it above, we get a type error
          switchMap(handledError => throwError(() => handledError)),
        );
      }
      return throwError(() => error);
    }),
  );
};
